import { useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import { homeFor } from "@/utils/roles";
import { Toaster } from "@/components/ui/sonner";

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(registration => {
        console.log('SW registered:', registration.scope);
      })
      .catch(error => {
        console.log('SW registration failed:', error);
      });
  });
}

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import ActivateInvite from "@/pages/ActivateInvite";
import { VerifyEmail, VerifyEmailPending, ForgotPassword, ResetPassword, LegalPage } from "@/pages/AuthExtras";
import { SubscriptionBanner } from "@/components/subscription/SubscriptionBanner";
import MenuEditor from "@/pages/MenuEditor";
import LayoutEditor from "@/pages/LayoutEditor";
import POSPanel from "@/pages/POSPanel";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import CustomerMenu from "@/pages/CustomerMenu";
import CustomerBill from "@/pages/CustomerBill";
import SuperAdminPanel from "@/pages/SuperAdminPanel";
import KitchenView from "@/pages/KitchenView";
import KitchenTV from "@/pages/KitchenTV";
import CashierView from "@/pages/CashierView";
import WaiterView from "@/pages/WaiterView";
import Promotions from "@/pages/Promotions";
import VisualMap from "@/pages/VisualMap";
import Reservations from "@/pages/Reservations";
import CashRegisterPage from "@/pages/CashRegisterPage";
import InventoryPage from "@/pages/InventoryPage";
import ShiftsPage from "@/pages/ShiftsPage";
import QRManager from "@/pages/QRManager";
import QRCenter from "@/pages/QRCenter";
import PaymentPage from "@/pages/PaymentPage";
import BillingPage from "@/pages/BillingPage";
import CuentaComensalPage from "@/pages/CuentaComensalPage";
import TemplateDesigner from "@/pages/TemplateDesigner";
import IpDiagnostics, { IpTestVisit } from "@/pages/IpDiagnostics";
import ProfilePage from "@/pages/ProfilePage";
import { AppearanceSync } from "@/components/AppearanceSync";
import { ModernShell } from "@/modern/ModernShell";
import RentalsPrototype from "@/modern/admin/RentalsPrototype";

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { processGoogleSession } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      processGoogleSession(sessionId)
        .then((user) => {
          // Clear the hash and navigate
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { state: { user }, replace: true });
        })
        .catch((error) => {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate, processGoogleSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Procesando autenticación...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ADMIN = ['business_admin'];
const CASH = ['business_admin', 'cashier'];
const STAFF = ['business_admin', 'cashier', 'waiter'];
const ALL = ['business_admin', 'cashier', 'waiter', 'kitchen'];

const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && user?.role !== 'super_admin' && !roles.includes(user?.role)) {
    return <Navigate to={homeFor(user)} replace />;
  }

  if (user?.email_verified === false) {
    return <Navigate to="/verificar-email" replace />;
  }

  return <><SubscriptionBanner /><ModernShell>{children}</ModernShell></>;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      {/* Customer Routes (Public - QR scan) */}
      <Route path="/menu/:qrCode" element={<CustomerMenu />} />
      <Route path="/bill/:sessionId" element={<CustomerBill />} />
      <Route path="/pay/:sessionId/:splitId" element={<PaymentPage />} />
      <Route path="/cuenta/:codigo" element={<CuentaComensalPage />} />
      <Route path="/prueba-ip/:token" element={<IpTestVisit />} />
      <Route path="/diagnostico-ip" element={<ProtectedRoute roles={ADMIN}><IpDiagnostics /></ProtectedRoute>} />
      <Route path="/perfil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/activar/:token" element={<ActivateInvite />} />
      <Route path="/verificar-email/:token" element={<VerifyEmail />} />
      <Route path="/verificar-email" element={<VerifyEmailPending />} />
      <Route path="/recuperar" element={<ForgotPassword />} />
      <Route path="/restablecer/:token" element={<ResetPassword />} />
      <Route path="/terminos" element={<LegalPage doc="terminos" />} />
      <Route path="/privacidad" element={<LegalPage doc="privacidad" />} />
      <Route path="/onboarding" element={<ProtectedRoute roles={ADMIN}><Onboarding /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute roles={ADMIN}><WebSocketProvider><Dashboard /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/super-admin" element={<ProtectedRoute roles={[]}><SuperAdminPanel /></ProtectedRoute>} />
      <Route path="/menu-editor" element={<ProtectedRoute roles={ADMIN}><MenuEditor /></ProtectedRoute>} />
      <Route path="/layout-editor" element={<ProtectedRoute roles={ADMIN}><LayoutEditor /></ProtectedRoute>} />
      <Route path="/layout-editor/:spaceId" element={<ProtectedRoute roles={ADMIN}><LayoutEditor /></ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute roles={STAFF}><WebSocketProvider><POSPanel /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute roles={ADMIN}><Settings /></ProtectedRoute>} />
      <Route path="/promotions" element={<ProtectedRoute roles={ADMIN}><Promotions /></ProtectedRoute>} />
      <Route path="/visual-map" element={<ProtectedRoute roles={ADMIN}><WebSocketProvider><VisualMap /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/reservations" element={<ProtectedRoute roles={STAFF}><Reservations /></ProtectedRoute>} />
      <Route path="/cash-register" element={<ProtectedRoute roles={CASH}><CashRegisterPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute roles={ADMIN}><InventoryPage /></ProtectedRoute>} />
      <Route path="/shifts" element={<ProtectedRoute roles={ADMIN}><ShiftsPage /></ProtectedRoute>} />
      <Route path="/qr-manager" element={<ProtectedRoute roles={ADMIN}><QRManager /></ProtectedRoute>} />
      <Route path="/qr-center" element={<ProtectedRoute roles={ADMIN}><QRCenter /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute roles={ADMIN}><Reports /></ProtectedRoute>} />
      <Route path="/kitchen" element={<ProtectedRoute roles={ALL}><WebSocketProvider><KitchenView /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/cashier" element={<ProtectedRoute roles={CASH}><WebSocketProvider><CashierView /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/waiter" element={<ProtectedRoute roles={STAFF}><WebSocketProvider><WaiterView /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/kitchen-tv" element={<ProtectedRoute roles={ALL}><WebSocketProvider><KitchenTV /></WebSocketProvider></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute roles={CASH}><BillingPage /></ProtectedRoute>} />
      <Route path="/alquileres" element={<ProtectedRoute roles={[]}><RentalsPrototype /></ProtectedRoute>} />
      <Route path="/template-designer" element={<ProtectedRoute roles={ADMIN}><TemplateDesigner /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppearanceSync />
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
